import React, { FunctionComponent, useEffect, useState } from "react";
import { CertificateInfo, HsmMeta, ModuleCode, signActions, SigningData, Status } from "@mc/react-sign";
import { Signing, StatusChange } from "@mc/react-sign/dist/action/signActions";
import SignApi from "../../api/gen/qsign/SignApi";
import { PrimaryButton, SecondaryButton, DialogBody, DialogFooter, Col, H4, H2 } from "@mc/ui-v3";
import { Card, CardBlock, D4, Dialog, ModalComponentProps, ProgressBar } from "@mc/react-core";
import { Modal } from "reactstrap";
import { RedButton } from "../RedButton";
import './PrubehPodepisovaniModal.scss';

type QSigningData = {
  "@class": string;
  viewId: string;
  documentId?: string;
  returnUrl?: string;
  stampMetadata?: SignatureStamp;
} & SigningData;

type SignatureStamp = {
  x: number;
  y: number;
  zoom: number;
  rotation: bigint;
  pageNumber: bigint;
};

type Props = {
  viewId: string;
  documentId?: string;
  cert: CertificateInfo;
  signatureStamp?: SignatureStamp;
  returnUrl: string;
  vse: boolean;
  onSuccess: () => void;
  onFail: () => void;
} & ModalComponentProps;

const containsStatus = (status: Status, statuses: Status[]): boolean => {
  return statuses.indexOf(status) >= 0;
};

export const PrubehPodepisovaniModal: FunctionComponent<Props> = ({
  onSuccess,
  onFail,
  viewId,
  documentId,
  vse,
  cert,
  returnUrl,
  onClose,
  signatureStamp,
}) => {
  const [signing, setSigning] = useState<Signing>(null);
  const [statusChange, setStatusChange] = useState<StatusChange>(null);
  const [canceling, setCanceling] = useState<boolean>(false);
  const [animate, setAnimate] = useState<boolean>(true);
  const [wasOpen, setWasOpen] = useState<boolean>(false);
  const [HSMMeta, setHSMMeta] = useState<HsmMeta>(null);

  const submitting = canceling;

  const handleOpenHsm = (url) => {
      const finalUrl = url || HSMMeta.url;
      console.log('open hsm url', finalUrl);
      window.open(finalUrl, "_blank");
  }

  const onStatusChange = (statusChange) => {
    if (statusChange.status == Status.FINISH) {
        onSuccess();
        onClose();
      setAnimate(false);
    } else if (statusChange.status == Status.CANCEL) {
      setCanceling(false);
    }

    setStatusChange(statusChange);
    setHSMMeta(statusChange.moduleData);
    if (statusChange.status == Status.WAITING) {
        setWasOpen(true);
    }
  };

  useEffect(() => {
      if (HSMMeta) {
          handleOpenHsm(HSMMeta?.url);
      }
  }, [wasOpen]);

  const onCloseExt = (e) => {
    if (statusChange) {
      const status = statusChange.status;
      if (["ERROR"].indexOf(status) >= 0) {
        onFail();
      }
    }
    onClose();
  };

  const onCancel = () => {
    signing.cancel();
    setCanceling(true);
  };

  useEffect(() => {
    if (!cert) {
      setStatusChange({
        status: Status.ERROR,
        error: "Nelze zahájit podepisování. Je potřeba nejprve nastavit certifikát.",
        progress: 0,
        signingId: "",
      });
      return;
    }

    const signing = signActions.createSigning(
      onStatusChange,
      (s) => SignApi.startSigning(s),
      (s) => SignApi.statusSigning(s),
      (s) => SignApi.cancelSigning(s),
      (error) => {
        console.error("fatální selhání", error);
      }
    );

    const data: QSigningData = {
      "@class": "cz.marbes.qsign.controller.vo.QSigningData",
      viewId: viewId,
      documentId: documentId,
      returnUrl: returnUrl,
      stampMetadata: signatureStamp,
    };

    signing.start(cert, data);

    setSigning(signing);
    setStatusChange(signing.status());
  }, []);

  const stopStatuses = [Status.CANCEL, Status.ERROR];
  const isStopped = containsStatus(statusChange && statusChange.status, stopStatuses);

  const getMsg = () => {
      if (statusChange) {
          const type = cert.moduleCode == ModuleCode.HSM ? "Odesláno" : "Podepsáno";
          if (statusChange.signed == null) {
              return statusChange.message;
          } else {
              return statusChange.message + " " + type + " " + statusChange.signed + " z " + statusChange.total + ".";
          }
      } else {
          return "";
      }
  }

  return (
    <Dialog
      isCloseButtonShown={false}
      canEscapeKeyClose
      backdropClassName={"prubeh-podepisovani-custom"}
      autoFocus
      canOutsideClickClose={false}
      isOpen
      style={{ maxWidth: "500px" }}
      title={<D4>{"Podepisování dokumentů"}</D4>}
      onClose={onCloseExt}
      usePortal={false}
      className="w-100"
    >

      <div className={"bp3-dialog-body mb-0 mt-0"}>
        <DialogBody>
          <Card className="bg-background-2">
            {statusChange && (
              <CardBlock>
                {
                  <div className="pb-1">
                      {getMsg()}
                  </div>
                }
                <ProgressBar
                  intent={statusChange.status == Status.ERROR ? "danger" : "primary"}
                  animate={animate}
                  value={vse ? statusChange.progress / 100 : 100}
                  stripes={animate && !isStopped}
                />
                {statusChange.error && (
                  <div style={{ overflowWrap: "anywhere" }} className="pt-1 font-weight-bold">
                    {statusChange.error}
                  </div>
                )}
              </CardBlock>
            )}
          </Card>
        </DialogBody>
        {statusChange && (
          <DialogFooter>
            {!isStopped && statusChange.progress != 100 && (
              <RedButton type={"button"} onClick={onCancel} disabled={submitting}>
                Přerušit podepisování
              </RedButton>
            )}
            {isStopped && (
              <SecondaryButton disabled={submitting} onClick={onCloseExt} className={"ml-3"}>
                Zavřít
              </SecondaryButton>
            )}
            {statusChange.progress == 100 && statusChange.status == Status.FINISH && (
              <PrimaryButton disabled={submitting} onClick={vse ? onSuccess : onCloseExt} className={"ml-3"}>
                Dokončit
              </PrimaryButton>
            )}
            {statusChange.status == Status.WAITING && (
              <div>
                <RedButton type={"button"} onClick={onCancel} disabled={submitting}>
                  Přerušit podepisování
                </RedButton>

                <PrimaryButton
                  onClick={() => {
                    window.open(HSMMeta.url, "_blank");
                  }}
                >
                  Přejít na HSM
                </PrimaryButton>
              </div>
            )}
          </DialogFooter>
        )}
      </div>
    </Dialog>
  );
};
